import * as React from "react";
import { Tag } from "lucide-react";
import { Link } from "react-router";
import { useToggle } from "react-use";

import { useExcludeContractFamilyReviewDocumentMutation } from "@/api/contract-family-pipeline";
import {
  useLinkContractFamilyDocumentMutation,
  useMarkContractFamilyOrphanMutation,
  useOverrideContractFamilyRoleMutation,
} from "@/api/contract-families";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table/table";
import { cn } from "@/lib/utils";
import { ContractFamilyRole, RoleUpdatePayload } from "@/types/contract-family";

import {
  ContractFamilyDocumentViewModel,
  ContractFamilyGroupViewModel,
  ContractFamilyStatusTone,
} from "../contractFamilyViewModels";
import { buildPipelineReturnUrl } from "../pipeline/stageNavigation";
import { ContractFamilyMoveMenu } from "./ContractFamilyMoveMenu";
import { ContractFamilyRoleMenu } from "./ContractFamilyRoleMenu";
import { ParentOption, RoleOverrideDialog } from "./RoleOverrideDialog";

export interface ContractFamilyMembershipChange {
  documentId: string;
  fromFamilyId: string;
  toFamilyId: string | null;
}

interface Props {
  document: ContractFamilyDocumentViewModel;
  selected: boolean;
  indent?: boolean;
  families?: ContractFamilyGroupViewModel[];
  onSelectDocument: (document: ContractFamilyDocumentViewModel) => void;
  onMembershipChanged?: (change: ContractFamilyMembershipChange) => void;
  pipelineRunId?: string | null;
  onPipelineDocumentExcluded?: () => void;
  readOnly?: boolean;
}

const badgeVariantByTone: Record<ContractFamilyStatusTone, React.ComponentProps<typeof Badge>["variant"]> = {
  neutral: "outline",
  success: "traffic-green",
  warning: "traffic-amber",
  danger: "traffic-red",
  info: "secondary",
};

export const ContractFamilyDocumentRow: React.FC<Props> = ({
  document,
  selected,
  indent = false,
  families = [],
  onSelectDocument,
  onMembershipChanged,
  pipelineRunId,
  onPipelineDocumentExcluded,
  readOnly = false,
}) => {
  const [linkDocument] = useLinkContractFamilyDocumentMutation();
  const [markOrphan] = useMarkContractFamilyOrphanMutation();
  const [overrideRole] = useOverrideContractFamilyRoleMutation();
  const [excludeDocument] = useExcludeContractFamilyReviewDocumentMutation();
  const [pendingAction, setPendingAction] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [isRoleDialogOpen, toggleRoleDialog] = useToggle(false);
  const isPending = pendingAction !== null;
  const parentOptions = React.useMemo<ParentOption[]>(
    () =>
      families.flatMap(family =>
        family.master && family.master.documentId !== document.documentId
          ? [{ documentId: family.master.documentId, label: family.name }]
          : [],
      ),
    [document.documentId, families],
  );
  const roleDialogTarget = React.useMemo(
    () => ({
      documentId: document.documentId,
      name: document.displayName,
      role: document.role,
      parentDocumentId: document.parentDocumentId,
    }),
    [document.displayName, document.documentId, document.parentDocumentId, document.role],
  );

  const runAction = async (actionName: string, action: () => Promise<void>, failureMessage = "Action failed") => {
    if (isPending) {
      return;
    }

    setPendingAction(actionName);
    setErrorMessage(null);

    try {
      await action();
    } catch {
      setErrorMessage(failureMessage);
    } finally {
      setPendingAction(null);
    }
  };

  const handleMoveToFamily = ({ familyId, parentDocumentId }: { familyId: string; parentDocumentId: string }) =>
    runAction("move", async () => {
      await linkDocument({
        documentId: document.documentId,
        payload: {
          parent_document_id: parentDocumentId,
          comment: null,
        },
      }).unwrap();
      onMembershipChanged?.({
        documentId: document.documentId,
        fromFamilyId: document.familyId,
        toFamilyId: familyId,
      });
    });

  const handleMoveToUngrouped = () =>
    runAction("ungroup", async () => {
      await markOrphan({
        documentId: document.documentId,
        payload: {
          comment: null,
        },
      }).unwrap();
      onMembershipChanged?.({
        documentId: document.documentId,
        fromFamilyId: document.familyId,
        toFamilyId: null,
      });
    });

  const handleChangeRole = (role: ContractFamilyRole) =>
    runAction("role", async () => {
      await overrideRole({
        documentId: document.documentId,
        payload: {
          role,
          parent_document_id: role === "MASTER" ? null : document.parentDocumentId,
          comment: null,
        },
      }).unwrap();
    });

  const handleMasterRoleSubmit = async (payload: RoleUpdatePayload) => {
    let saved = false;
    await runAction("role", async () => {
      await overrideRole({ documentId: document.documentId, payload }).unwrap();
      saved = true;
    });
    if (saved) {
      toggleRoleDialog(false);
    }
  };
  const canOpenQAndA = Boolean(pipelineRunId && document.fseId);
  const canExclude =
    !readOnly &&
    Boolean(
      pipelineRunId &&
      document.fseId &&
      document.role !== "MASTER" &&
      !document.familyId.startsWith("unassigned:") &&
      !document.familyId.startsWith("review:"),
    );
  const handleExclude = () => {
    if (!pipelineRunId || !canExclude) return;

    return runAction(
      "exclude",
      async () => {
        await excludeDocument({
          runId: pipelineRunId,
          familyId: document.familyId,
          documentId: document.documentId,
        }).unwrap();
        onPipelineDocumentExcluded?.();
      },
      "Unable to exclude document.",
    );
  };

  return (
    <>
      <TableRow data-state={selected ? "selected" : undefined} className="h-12">
        <TableCell className="w-[46%] px-3 py-2">
          <button
            type="button"
            className={cn(
              "block min-w-0 text-left text-sm font-medium text-slate-950 hover:text-blue-700",
              indent && "pl-5",
            )}
            onClick={() => onSelectDocument(document)}
          >
            <span className="block truncate">{document.displayName}</span>
            <span className="block truncate font-mono text-[11px] font-normal text-slate-400">
              {document.documentId}
            </span>
          </button>
          <details className={cn("mt-1 text-xs text-slate-500", indent && "ml-5")}>
            <summary className="w-fit cursor-pointer select-none font-medium text-slate-600 hover:text-slate-900">
              Ingestion summary
            </summary>
            <p className="mt-1 max-w-2xl whitespace-pre-wrap break-words text-slate-600">
              {document.ingestionSummary?.trim() || "No ingestion summary available."}
            </p>
          </details>
        </TableCell>
        <TableCell className="w-[14%] px-3 py-2">
          <Badge variant="outline" className="rounded-md px-2 py-0 text-[11px] uppercase">
            {document.roleLabel}
          </Badge>
        </TableCell>
        <TableCell className="w-[16%] px-3 py-2">
          <Badge variant={badgeVariantByTone[document.statusTone]} className="rounded-md px-2 py-0 text-[11px]">
            {document.statusLabel}
          </Badge>
        </TableCell>
        <TableCell className="w-[24%] px-3 py-2">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {errorMessage && (
              <span role="alert" className="text-xs text-red-700">
                {errorMessage}
              </span>
            )}
            {canOpenQAndA && pipelineRunId && document.fseId ? (
              <Button asChild size="xs" variant="outline">
                <Link
                  to={`/chat/${encodeURIComponent(document.fseId)}`}
                  state={{ fromSnapshot: buildPipelineReturnUrl(pipelineRunId, "categorise_review_confirm") }}
                >
                  Go to Q&amp;A
                </Link>
              </Button>
            ) : null}
            {canExclude ? (
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={isPending}
                onClick={() => void handleExclude()}
              >
                Exclude
              </Button>
            ) : null}
            {!readOnly ? (
              <ContractFamilyMoveMenu
                document={document}
                families={families}
                pending={isPending}
                onMoveToFamily={handleMoveToFamily}
                onMoveToUngrouped={handleMoveToUngrouped}
              />
            ) : null}
            {!readOnly && document.role === "MASTER" ? (
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={isPending}
                onClick={() => toggleRoleDialog(true)}
              >
                <Tag className="mr-1 h-3.5 w-3.5" />
                Role
              </Button>
            ) : !readOnly ? (
              <ContractFamilyRoleMenu document={document} pending={isPending} onChangeRole={handleChangeRole} />
            ) : null}
          </div>
        </TableCell>
      </TableRow>
      {!readOnly ? (
        <RoleOverrideDialog
          open={isRoleDialogOpen}
          target={roleDialogTarget}
          parentOptions={parentOptions}
          isSubmitting={isPending}
          onOpenChange={toggleRoleDialog}
          onSubmit={handleMasterRoleSubmit}
        />
      ) : null}
    </>
  );
};
